import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../assets/logo-mudif.png";
import {
  X,
  User,
  LogIn,
  LayoutDashboard,
  LogOut,
  ChevronDown,
  FolderOpen,
  Home,
  ArrowRight,
  Search,
} from "lucide-react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "../lib/firebase";
import { listSections } from "../lib/sectionsApi";

function cx(...s) {
  return s.filter(Boolean).join(" ");
}

const FALLBACK_LINKS = [
  { to: "/", label: "Home", kind: "home" },
  { to: "/archivi-900", label: "Archivi ’900", kind: "section" },
  { to: "/fondi", label: "Fondi", kind: "section" },
  { to: "/album-foto", label: "Album Fotografici", kind: "section" },
  { to: "/album-di-famiglia", label: "Album di Famiglia", kind: "section" },
];

function routeFromSection(section) {
  const slug = section?.slug || "";
  return slug ? `/${slug}` : "/";
}

function SectionDropdown({
  sections = [],
  onNavigate,
  onOpenChange,
  forceOpen = false,
  onForceOpenConsumed,
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      onForceOpenConsumed?.();
    }
  }, [forceOpen, onForceOpenConsumed]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useEffect(() => {
    return () => onOpenChange?.(false);
  }, [onOpenChange]);

  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      const t = e.target;
      if (btnRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const currentIsSection = sections.some((s) => routeFromSection(s) === location.pathname);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className={cx(
          "group inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-extrabold tracking-tight transition",
          currentIsSection || open
            ? "bg-slate-900 text-white shadow-[0_18px_60px_rgba(2,6,23,.14)]"
            : "text-slate-700 hover:bg-slate-900/[0.04]"
        )}
        aria-expanded={open}
        aria-label="Apri elenco sezioni"
      >
        <FolderOpen className="h-4 w-4" />
        Sezioni
        <ChevronDown className={cx("h-4 w-4 transition", open ? "rotate-180" : "")} />
      </button>

      <div
        ref={panelRef}
        className={cx(
          "absolute right-0 top-full mt-3 w-[min(78vw,860px)] origin-top-right transition-all duration-200",
          open ? "scale-100 opacity-100" : "pointer-events-none scale-[0.98] opacity-0"
        )}
      >
        <div className="relative overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_30px_120px_rgba(2,6,23,.18)]">
          <div className="pointer-events-none absolute inset-0 opacity-100 [background:radial-gradient(700px_320px_at_10%_0%,rgba(204,58,33,.10),transparent_60%),radial-gradient(700px_320px_at_100%_100%,rgba(251,191,36,.08),transparent_62%)]" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />

          <div className="relative p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-black text-slate-900">
                  Esplora le sezioni dell’archivio
                </div>
                <div className="mt-1 text-xs font-semibold text-slate-600">
                  Tutte le categorie raccolte in un unico spazio ordinato
                </div>
              </div>

              <span className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1.5 text-[11px] font-black text-white">
                <FolderOpen className="h-3.5 w-3.5" />
                {sections.length} sezioni
              </span>
            </div>

            <div className="my-4 h-px w-full bg-gradient-to-r from-transparent via-[#CC3A21]/20 to-transparent" />

            <div className="max-h-[58vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3 xl:grid-cols-3">
                {sections.map((s) => {
                  const to = routeFromSection(s);
                  const active = location.pathname === to;

                  return (
                    <NavLink
                      key={s.slug}
                      to={to}
                      onClick={() => {
                        setOpen(false);
                        onNavigate?.();
                      }}
                      className={cx(
                        "group rounded-3xl border px-4 py-4 transition",
                        active
                          ? "border-slate-900/15 bg-white shadow-[0_18px_60px_rgba(2,6,23,.10)]"
                          : "border-slate-200 bg-slate-50 hover:-translate-y-[1px] hover:bg-white"
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="line-clamp-2 text-sm font-black tracking-tight text-slate-900">
                            {s.label}
                          </div>
                         
                        </div>

                        <span
                          className={cx(
                            "mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full transition",
                            active
                              ? "bg-[#CC3A21] shadow-[0_0_0_6px_rgba(204,58,33,.12)]"
                              : "bg-slate-300 group-hover:bg-[#CC3A21]"
                          )}
                        />
                      </div>

                      <div className="mt-4 inline-flex items-center gap-2 text-[12px] font-black text-slate-700 group-hover:text-[#CC3A21]">
                        Apri sezione
                        <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
                      </div>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="h-1.5 w-full bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />
        </div>
      </div>
    </div>
  );
}

function UserMenu({ user }) {
  const rawName = (user?.displayName || "").trim();
  const friendlyName = rawName || "Admin";

  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      const t = e.target;
      if (btnRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const doLogout = async () => {
    setOpen(false);
    await signOut(auth);
    navigate("/");
  };

  const email = user?.email || "";
  const shortEmail = email.length > 28 ? email.slice(0, 28) + "…" : email;

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className={cx(
          "inline-flex items-center gap-2 rounded-2xl",
          "bg-slate-900 text-white",
          "px-3 py-2.5 sm:px-4",
          "text-sm font-black",
          "shadow-[0_18px_60px_rgba(2,6,23,.18)]",
          "transition hover:-translate-y-[1px]",
          "max-w-[72vw] sm:max-w-none"
        )}
        aria-label="Menu utente"
        aria-expanded={open}
      >
        <span className="grid h-8 w-8 place-items-center rounded-xl bg-white/10 ring-1 ring-white/15">
          <User className="h-4.5 w-4.5 text-white" />
        </span>

        <span className="min-w-0 leading-tight">
          {user ? (
            <>
              <span className="block max-w-[44vw] truncate sm:max-w-none">
                Benvenuto, {friendlyName}
              </span>
              <span className="hidden text-[11px] font-extrabold text-white/70 sm:block">
                Apri menu
              </span>
            </>
          ) : (
            <>
              <span className="block max-w-[44vw] truncate sm:max-w-none">Accedi</span>
              <span className="hidden text-[11px] font-extrabold text-white/70 sm:block">
                Login admin
              </span>
            </>
          )}
        </span>

        <ChevronDown
          className={cx(
            "ml-1 h-4 w-4 shrink-0 opacity-90 transition",
            open ? "rotate-180" : ""
          )}
        />
      </button>

      <div
        ref={menuRef}
        className={cx(
          "absolute right-0 mt-3 w-[min(92vw,360px)] origin-top-right transition-all duration-200",
          open ? "scale-100 opacity-100" : "pointer-events-none scale-[0.98] opacity-0"
        )}
      >
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_30px_120px_rgba(2,6,23,.18)]">
          <div className="pointer-events-none absolute inset-0 opacity-90 [background:radial-gradient(520px_260px_at_12%_0%,rgba(204,58,33,.12),transparent_60%),radial-gradient(520px_260px_at_94%_120%,rgba(251,191,36,.08),transparent_62%)]" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />

          <div className="relative p-4">
            <div className="flex items-start gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-2xl bg-slate-900/[0.06] ring-1 ring-slate-900/10">
                <User className="h-5 w-5 text-slate-800" />
              </span>

              <div className="min-w-0">
                <div className="text-sm font-black text-slate-900">
                  {user ? `Bentornato, ${friendlyName} ✨` : "Ciao 👋"}
                </div>
                <div className="mt-1 truncate text-xs font-semibold text-slate-600">
                  {user ? shortEmail : "Accedi per aprire il pannello admin"}
                </div>
              </div>
            </div>

            <div className="my-4 h-px w-full bg-gradient-to-r from-transparent via-[#CC3A21]/20 to-transparent" />

            {user ? (
              <div className="space-y-2">
                <button
                  onClick={() => go("/admin")}
                  className={cx(
                    "w-full rounded-2xl px-4 py-3",
                    "flex items-center justify-between",
                    "bg-slate-50 ring-1 ring-slate-200",
                    "text-sm font-extrabold text-slate-900",
                    "transition hover:-translate-y-[1px] hover:bg-white"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <LayoutDashboard className="h-4 w-4" />
                    Pannello Admin
                  </span>
                  <span className="h-2 w-2 rounded-full bg-[#CC3A21]/80" />
                </button>

                <button
                  onClick={doLogout}
                  className={cx(
                    "w-full rounded-2xl px-4 py-3",
                    "flex items-center justify-between",
                    "bg-rose-50 ring-1 ring-rose-200",
                    "text-sm font-extrabold text-rose-900",
                    "transition hover:-translate-y-[1px] hover:bg-rose-100"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <LogOut className="h-4 w-4" />
                    Logout
                  </span>
                  <span className="h-2 w-2 rounded-full bg-rose-500/70" />
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => go("/login")}
                  className={cx(
                    "w-full rounded-2xl px-4 py-3",
                    "flex items-center justify-between",
                    "bg-slate-900 text-white",
                    "text-sm font-extrabold",
                    "shadow-[0_18px_60px_rgba(2,6,23,.16)]",
                    "transition hover:-translate-y-[1px]"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <LogIn className="h-4 w-4" />
                    Login
                  </span>
                  <span className="h-2 w-2 rounded-full bg-[#CC3A21]" />
                </button>
              </div>
            )}
          </div>

          <div className="h-1.5 w-full bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const [forceOpenSections, setForceOpenSections] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const progressRef = useRef(null);
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [sections, setSections] = useState([]);
  const [mobileQuery, setMobileQuery] = useState("");

  const closeMenu = () => setMenuOpen(false);
  const toggleMenu = () => setMenuOpen((v) => !v);

  useEffect(() => {
    const openArchiveNavigation = () => {
      const isMobile = window.innerWidth < 1024;

      window.scrollTo({ top: 0, behavior: "smooth" });

      if (isMobile) {
        setForceOpenSections(false);
        setMenuOpen(true);
      } else {
        setMenuOpen(false);
        setForceOpenSections(true);
      }
    };

    window.addEventListener("mudif:open-sections", openArchiveNavigation);

    return () => {
      window.removeEventListener("mudif:open-sections", openArchiveNavigation);
    };
  }, []);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      if (location.pathname === "/login") {
        setUser(null);
        return;
      }
      setUser(u || null);
    });

    return () => unsub();
  }, [location.pathname]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const data = await listSections();
        if (!alive) return;

        const normalized = (data || [])
          .filter((s) => s?.visible !== false)
          .map((s) => ({
            slug: s.slug,
            label: s.label,
          }))
          .filter((s) => s.slug && s.label);

        setSections(normalized);
      } catch (e) {
        console.error("Errore caricando sezioni navbar:", e);
        if (!alive) return;

        const fallbackSections = FALLBACK_LINKS
          .filter((l) => l.kind === "section")
          .map((l) => ({
            slug: l.to.replace(/^\//, ""),
            label: l.label,
          }));

        setSections(fallbackSections);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);

      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const p = docHeight > 0 ? (y / docHeight) * 100 : 0;
      if (progressRef.current) progressRef.current.style.width = `${p}%`;
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = prev || "");
  }, [menuOpen]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const rawName = (user?.displayName || "").trim().split(/\s+/)[0] || "";
  const friendlyName = rawName || "Admin";

  const homeLink = useMemo(
    () => FALLBACK_LINKS.find((l) => l.kind === "home") || { to: "/", label: "Home" },
    []
  );

  const filteredMobileSections = useMemo(() => {
    const q = mobileQuery.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter(
      (s) => s.label.toLowerCase().includes(q) || s.slug.toLowerCase().includes(q)
    );
  }, [sections, mobileQuery]);

  const navbarSolid = menuOpen || sectionsOpen;
  const navbarScrolled = scrolled && !navbarSolid;

  return (
    <>
      <style>{`
        .welcome-open .app-navbar,
        .welcome-open .nb-fab,
        .welcome-open .navbar-progress { display:none !important; }
      `}</style>

      <div className="navbar-progress fixed left-0 right-0 top-0 z-[80] h-[2px] bg-transparent">
        <span
          ref={progressRef}
          className="block h-full w-0 bg-[linear-gradient(90deg,rgba(204,58,33,.78),rgba(229,142,124,.62),rgba(242,201,165,.48),rgba(15,23,42,.20))]"
        />
      </div>

<header className="app-navbar fixed left-0 right-0 top-0 z-[75]">        <div
          className={cx(
            "relative w-full border-b transition-all duration-300",
            navbarSolid
              ? "border-slate-200 bg-white shadow-[0_20px_70px_rgba(2,6,23,.14)]"
              : navbarScrolled
              ? "border-slate-200/80 bg-white/92 backdrop-blur-xl shadow-[0_18px_70px_rgba(2,6,23,.08)]"
              : "border-slate-900/10 bg-white/88 backdrop-blur-xl shadow-[0_18px_70px_rgba(2,6,23,.06)]",
            scrolled ? "py-2" : "py-3"
          )}
        >
          <div
            className={cx(
              "pointer-events-none absolute inset-0 transition-opacity duration-300",
              navbarSolid
                ? "opacity-100 [background:radial-gradient(680px_240px_at_10%_0%,rgba(204,58,33,.08),transparent_60%),radial-gradient(680px_240px_at_90%_120%,rgba(251,191,36,.06),transparent_62%)]"
                : "opacity-80 [background:radial-gradient(680px_240px_at_10%_0%,rgba(204,58,33,.10),transparent_60%),radial-gradient(680px_240px_at_90%_120%,rgba(251,191,36,.08),transparent_62%)]"
            )}
          />

          <div className="relative mx-auto flex w-[min(1380px,96%)] items-center justify-between gap-4">
            <NavLink
              to="/"
              onClick={closeMenu}
              className="flex min-w-0 items-center gap-3 rounded-2xl px-2 py-2 transition hover:bg-slate-900/[0.03]"
              aria-label="Vai alla Home"
            >
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-slate-900/[0.04] ring-1 ring-slate-900/10 shadow-[0_14px_40px_rgba(2,6,23,.08)]">
                <img src={logo} alt="MuDiF" className="h-8 w-8 object-contain" />
              </div>

              <div className="hidden min-w-0 leading-tight sm:block">
                <div className="truncate text-sm font-black tracking-tight text-slate-900">
                  Archivio MuDiF
                </div>
                <div className="truncate text-xs font-semibold text-slate-600">
                  Museo Didattico della Fotografia
                </div>
              </div>
            </NavLink>

            <nav className="hidden items-center gap-2 lg:flex">
              <NavLink
                to={homeLink.to}
                className={({ isActive }) =>
                  cx(
                    "group relative inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-extrabold tracking-tight transition hover:bg-slate-900/[0.03]",
                    isActive ? "text-slate-900" : "text-slate-700"
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Home className="h-4 w-4" />
                    <span className="relative z-10">{homeLink.label}</span>
                    <span
                      className={cx(
                        "pointer-events-none absolute inset-x-3 -bottom-1 h-[3px] rounded-full bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)] transition-all duration-300",
                        isActive ? "opacity-90" : "opacity-0 group-hover:opacity-60"
                      )}
                    />
                  </>
                )}
              </NavLink>

              <SectionDropdown
                sections={sections}
                onNavigate={closeMenu}
                onOpenChange={setSectionsOpen}
                forceOpen={forceOpenSections}
                onForceOpenConsumed={() => setForceOpenSections(false)}
              />
            </nav>

            <div className="flex items-center gap-2">
              <div className="hidden lg:block">
                <UserMenu user={user} />
              </div>

              <div className="flex items-center gap-2 lg:hidden">
                <div className="hidden items-center gap-2 rounded-2xl bg-slate-900/[0.04] px-3 py-2 ring-1 ring-slate-900/10 xs:flex">
                  <User className="h-4 w-4 text-slate-700" />
                  <span className="text-xs font-black text-slate-700">
                    {user ? `Benvenuto, ${friendlyName}` : "Ospite"}
                  </span>
                </div>

                <button
                  onClick={toggleMenu}
                  className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-black text-white shadow-[0_18px_60px_rgba(2,6,23,.18)] transition hover:-translate-y-[1px]"
                  aria-label={menuOpen ? "Chiudi menu" : "Apri menu"}
                  aria-expanded={menuOpen}
                >
                  <span className="h-2 w-2 rounded-full bg-[#CC3A21] shadow-[0_10px_25px_rgba(204,58,33,.18)]" />
                  Menu
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <button
        type="button"
        aria-hidden={!menuOpen}
        onClick={closeMenu}
        className={cx(
          "fixed inset-0 z-[80] cursor-default transition-opacity",
          menuOpen ? "opacity-100" : "pointer-events-none opacity-0"
        )}
      >
        <span className="absolute inset-0 bg-white/70 backdrop-blur-[10px]" />
        <span className="absolute inset-0 opacity-90 [background:radial-gradient(900px_420px_at_20%_0%,rgba(204,58,33,.14),transparent_60%),radial-gradient(900px_420px_at_92%_85%,rgba(251,191,36,.10),transparent_60%)]" />
      </button>

      <aside
        aria-hidden={!menuOpen}
        className={cx(
          "fixed right-0 top-0 z-[90] h-[100dvh] w-[min(92vw,430px)] transition-transform duration-300 ease-out",
          menuOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="relative h-full overflow-hidden border-l border-slate-200 bg-white text-slate-900 shadow-[0_30px_120px_rgba(2,6,23,.18)]">
          <div className="absolute inset-0 opacity-95 [background:radial-gradient(720px_480px_at_12%_0%,rgba(204,58,33,.14),transparent_60%),radial-gradient(720px_480px_at_96%_72%,rgba(251,191,36,.10),transparent_62%),radial-gradient(560px_420px_at_20%_95%,rgba(229,142,124,.08),transparent_62%)]" />
          <div className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(to_right,rgba(2,6,23,.10)_1px,transparent_1px),linear-gradient(to_bottom,rgba(2,6,23,.07)_1px,transparent_1px)] [background-size:72px_72px]" />
          <div className="absolute inset-x-0 top-0 h-1 bg-[linear-gradient(90deg,#CC3A21,#E58E7C,#F2C9A5)]" />

          <div className="relative flex items-center justify-between px-5 pt-6">
            <div className="flex min-w-0 items-center gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-slate-900/[0.04] ring-1 ring-slate-900/10 shadow-[0_14px_40px_rgba(2,6,23,.08)]">
                <img src={logo} alt="MuDiF" className="h-8 w-8 object-contain" />
              </div>
              <div className="min-w-0 leading-tight">
                <div className="truncate text-sm font-black tracking-tight">Archivio MuDiF</div>
                <div className="text-xs font-semibold text-slate-600">Navigazione</div>
              </div>
            </div>

            <button
              onClick={closeMenu}
              className="grid h-10 w-10 place-items-center rounded-2xl bg-slate-900/[0.04] ring-1 ring-slate-900/10 transition hover:bg-slate-900/[0.06]"
              aria-label="Chiudi menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="relative mt-6 h-[calc(100dvh-96px)] overflow-y-auto px-4 pb-7">
            <div className="space-y-4">
              <NavLink
                to={homeLink.to}
                onClick={closeMenu}
                className={({ isActive }) =>
                  cx(
                    "group flex items-center justify-between rounded-2xl bg-white px-4 py-4 ring-1 ring-slate-200 transition hover:-translate-y-[1px]",
                    isActive ? "shadow-[0_18px_60px_rgba(2,6,23,.10)]" : ""
                  )
                }
              >
                <span className="flex items-center gap-3 text-[15px] font-extrabold tracking-tight">
                  <Home className="h-4.5 w-4.5" />
                  {homeLink.label}
                </span>
                <span className="h-2 w-2 rounded-full bg-[#CC3A21]/60 group-hover:bg-[#CC3A21]" />
              </NavLink>

              <section className="rounded-[28px] border border-slate-200 bg-white p-4 shadow-[0_18px_60px_rgba(2,6,23,.10)]">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-black text-slate-900">Sezioni archivio</div>
                    <div className="mt-1 text-xs font-semibold text-slate-600">
                      Menu ordinato anche quando crescono
                    </div>
                  </div>

                  <span className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-3 py-1.5 text-[11px] font-black text-white">
                    <FolderOpen className="h-3.5 w-3.5" />
                    {sections.length}
                  </span>
                </div>

                <div className="relative mt-4">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={mobileQuery}
                    onChange={(e) => setMobileQuery(e.target.value)}
                    placeholder="Cerca sezione..."
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-10 py-3 text-sm font-semibold text-slate-800 outline-none placeholder:text-slate-400 focus:border-[#CC3A21]/30 focus:bg-white"
                  />
                </div>

                <div className="mt-4 max-h-[42vh] overflow-y-auto pr-1">
                  <div className="space-y-2">
                    {filteredMobileSections.length > 0 ? (
                      filteredMobileSections.map((s) => {
                        const to = routeFromSection(s);
                        return (
                          <NavLink
                            key={s.slug}
                            to={to}
                            onClick={closeMenu}
                            className={({ isActive }) =>
                              cx(
                                "group flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3 ring-1 ring-slate-200 transition hover:-translate-y-[1px] hover:bg-white",
                                isActive ? "bg-white shadow-[0_18px_60px_rgba(2,6,23,.10)]" : ""
                              )
                            }
                          >
                            <div className="min-w-0">
                              <div className="truncate text-[14px] font-extrabold tracking-tight text-slate-900">
                                {s.label}
                              </div>
                              
                            </div>
                            <span className="h-2 w-2 shrink-0 rounded-full bg-[#CC3A21]/60 group-hover:bg-[#CC3A21]" />
                          </NavLink>
                        );
                      })
                    ) : (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center text-sm font-semibold text-slate-500">
                        Nessuna sezione trovata.
                      </div>
                    )}
                  </div>
                </div>
              </section>

              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_18px_60px_rgba(2,6,23,.10)]">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-black text-slate-900">
                    {user ? `Benvenuto, ${friendlyName} 👋` : "Ciao 👋"}
                  </div>
                  <span
                    className={cx(
                      "rounded-full px-3 py-1 text-[11px] font-black ring-1",
                      user
                        ? "bg-[#CC3A21]/[0.08] text-[#7A2417] ring-[#CC3A21]/20"
                        : "bg-slate-100 text-slate-700 ring-slate-200"
                    )}
                  >
                    {user ? "loggato" : "ospite"}
                  </span>
                </div>

                <div className="mt-3 space-y-2">
                  {user ? (
                    <>
                      <NavLink
                        to="/admin"
                        onClick={closeMenu}
                        className="flex items-center justify-between rounded-2xl bg-slate-900 px-4 py-3 text-sm font-extrabold text-white shadow-[0_18px_60px_rgba(2,6,23,.16)]"
                      >
                        <span className="flex items-center gap-2">
                          <LayoutDashboard className="h-4 w-4" />
                          Pannello Admin
                        </span>
                        <span className="h-2 w-2 rounded-full bg-[#CC3A21]" />
                      </NavLink>

                      <button
                        onClick={async () => {
                          closeMenu();
                          await signOut(auth);
                        }}
                        className="flex w-full items-center justify-between rounded-2xl bg-rose-50 px-4 py-3 text-sm font-extrabold text-rose-900 ring-1 ring-rose-200"
                      >
                        <span className="flex items-center gap-2">
                          <LogOut className="h-4 w-4" />
                          Logout
                        </span>
                        <span className="h-2 w-2 rounded-full bg-rose-500/70" />
                      </button>
                    </>
                  ) : (
                    <NavLink
                      to="/login"
                      onClick={closeMenu}
                      className="flex items-center justify-between rounded-2xl bg-slate-900 px-4 py-3 text-sm font-extrabold text-white shadow-[0_18px_60px_rgba(2,6,23,.16)]"
                    >
                      <span className="flex items-center gap-2">
                        <LogIn className="h-4 w-4" />
                        Login
                      </span>
                      <span className="h-2 w-2 rounded-full bg-[#CC3A21]" />
                    </NavLink>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}